import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {useLayoutEffect, useState} from 'react'
import {Checklist, DataObjectId, Hub} from 'yougile-model'
import {ProgressBar} from '@components/common/base/progress-bar/progress-bar'
import {DragControl, dragPropsFromCtl} from '@containers/hooks-draggable'
import {cn} from '@containers/html-utils'
import {useChecklistsStore} from '../../checklists-store'
import {isPlaceholder} from '../../placeholder'
import {ItemFullId} from '../../types'
import {ChecklistItemControl} from './checklist-item'
import {ChecklistItemAdd} from './checklist-item-add'
import {
  ChecklistContext,
  ChecklistStore,
  isChecklistItem,
  useChecklistStore
} from './checklist-store'
import {ChecklistTitle} from './checklist-title'
import {ChecklistTitleEdit} from './checklist-title-edit'

type Props = {
  task: Hub
  list: Checklist
  checklistDragCtl: DragControl<DataObjectId>
  checklistItemDragCtl: DragControl<ItemFullId>
  className?: string
  needPaddings?: boolean
}

const ChecklistControlBase = observer((props: Props) => {
  const {task, list, checklistDragCtl, checklistItemDragCtl} = props
  const [checklistsState] = useChecklistsStore()
  const [state, store] = useChecklistStore()

  useLayoutEffect(() => {
    store.sync(task, list)
  }, [task, list])

  const handlers = dragPropsFromCtl(list.id, checklistDragCtl, 'move')
  const dragHandlers = checklistsState.editInProgress
    ? {
        onDragStart: (e: React.DragEvent) => {
          e.preventDefault()
        }
      }
    : handlers

  const anyDragged = !!checklistDragCtl.draggedId
  const showProgressBar = !anyDragged && !!list.items.length
  const showCheckboxes = !anyDragged && !!list.items.length
  const dragged = checklistDragCtl.draggedId === list.id

  return (
    <div
      draggable={!checklistsState.editInProgress}
      {...dragHandlers}
      className={cn(props.className, props.needPaddings ? 'mx-8' : '')}
    >
      {state.editing ? (
        <ChecklistTitleEdit list={list} />
      ) : (
        <ChecklistTitle list={list} dragged={dragged} anyDragged={anyDragged} />
      )}
      {showProgressBar && (
        <ProgressBar
          className={cn('flex-1 mb-8 mx-8', state.editing && 'mt-8')}
          value={store.getCompletedListCount()}
          maxValue={store.getListCount()}
          bgColor="background-sticker-panel"
        />
      )}
      {showCheckboxes && (
        <>
          {list.items.map(
            item =>
              isChecklistItem(item) && (
                <ChecklistItemControl
                  key={`${item.id}-${Number(isPlaceholder(item))}`}
                  task={task}
                  list={list}
                  item={item}
                  checklistItemDragCtl={checklistItemDragCtl}
                />
              )
          )}
        </>
      )}

      {!anyDragged && (
        <ChecklistItemAdd
          list={list}
          checklistItemDragCtl={checklistItemDragCtl}
          className={'mt-8 mb-8'}
        />
      )}
    </div>
  )
})

export const ChecklistControl = (props: Props) => {
  const [, checklistsStore] = useChecklistsStore()
  const [checklistStore] = useState(() => new ChecklistStore(checklistsStore))

  return (
    <ChecklistContext.Provider value={checklistStore}>
      <ChecklistControlBase {...props} />
    </ChecklistContext.Provider>
  )
}
